/**
 * The full-window overlay shown while OS files are dragged over the listing.
 *
 * Purely presentational. Whether it is visible is decided by a drag COUNTER in
 * the listing (dragenter/dragleave fire per element crossed, so a boolean would
 * flicker off the moment the pointer crossed into a child).
 *
 * `pointer-events-none` matters: the overlay must not swallow the drop itself.
 */

import { Upload as UploadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function DragOverlay({ visible }) {
  const { t } = useTranslation(['my-docs']);

  return (
    <>
      {visible && (
        <div
          className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center"
          style={{ background: 'rgba(76, 120, 223, 0.04)' }}
        >
          <div
            className="absolute inset-3 rounded-2xl"
            style={{
              border: '1.5px dashed rgba(76, 120, 223, 0.4)',
              animation: 'dragPulse 1.2s ease-in-out infinite',
            }}
          />

          <div
            className="flex items-center gap-3 px-6 py-4 rounded-2xl"
            style={{
              background: 'rgba(255, 255, 255, 0.88)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 8px 32px rgba(76, 120, 223, 0.12), 0 1px 4px rgba(0,0,0,0.06)',
              animation: 'cardIn 0.18s ease-out',
            }}
          >
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(76, 120, 223, 0.10)' }}
            >
              <UploadIcon className="w-4 h-4 text-brand" strokeWidth={2} />
            </div>
            <p className="text-sm font-semibold text-gray-700">{t('my-docs:dragDrop.title')}</p>
          </div>
        </div>
      )}

      {/* Kept mounted with the overlay's owner: the animations are referenced
          only from here, and Tailwind cannot express a custom keyframe. */}
      <style>{`
        @keyframes dragPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        @keyframes cardIn {
          from { opacity: 0; transform: scale(0.95) translateY(4px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </>
  );
}
